import { textChangeRangeIsUnchanged } from 'typescript';
import { ScreeningLight } from './screeningInvite';

export class ArgyleViewBag {
    // Identity Data
    identityData: IdentityViewBag;

    // Paystubs
    paystubData: ArgylePaystubViewBag[];
    summary: RIQIncomeSummary;
}

export class ArgyleViewBagResponse { 
    demBags: ArgyleViewBag[];
    streams : ArgyleIncomeStream[];
    paystubDataOCR : ArgyleOCRPaystubViewBag[];
    argyleInvite: InviteCreateRequest;
    sentLogs :ArgyleInviteSent[]
}

export class ArgyleStatusExplanation
{
    name : string;
    expl : string;
}
export class RIQIncomeSummary {
    monthlyGrossAverage: number;
    monthlyNetAverage: number;
}

export interface InviteCreateRequest {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    externalId: string;
    inviteId: string;
    userId: string;
    argyle_Url: string;
    postback_Url: string;
    inviteRevoked: boolean;
    inviteStatus: string;
    inviteRevokedDate: Date | string | null;
    inviteSentDate: Date | string | null;
    pmsPartnerUserId: number;
    locationName: string;
    accounts: string;
    screeningId: number;
    inviteSentLogs: ArgyleInviteSent[];
    _accts: string[];
    status: string;
}

export class ArgylePaystubViewBag {
    grossPay: number;
    grossPay_YTD: number;
    netPay: number;
    netPay_YTD: number;
    currency: string;
    employerName: string;
    employerAddress: Address;
    periodStartDate: Date | string;
    paystubDate: Date | string;
    periodEndDate: Date | string;
    paystubOrigin: ArgylePaystubSource;
    paystubOriginstr: string;
}

export class ArgyleInviteSent {
    id: number;
    dateSent: Date | string;
    attemptNumber: number;
    screeningId: number;
    inviteId: number;
    email: string;
    phone: string;
    userId: number;
    inviteStatus: string;
    inviteGuid: string;
    userName: string;
}

export class ArgyleOCRPaystubViewBag {
    paystubId: number;
    periodStartDate: Date | string;
    paystubDate: Date | string;
    periodEndDate: Date | string;
    payCycle: string;
    docType: string;
    authenticityScore: number;
    employerName: string;
    employeeName: string;
    grossPay: number;
    grossPay_YTD: number;
}

export enum ArgylePaystubSource { 
    PayrollSystemSync = 0,
    UserFormUpload = 1
}


export class AdditionalIncomeSource {
    id: number;
    screeningId: number;
    amount: number;
    incomeType: AdditionalIncomeSourceType;
    incomeSourceDescription: string;
    userId: number;
    createdDate: Date | string;
    userName : string;
    incomeVerified : boolean;
    maappedFromIncomeVer : boolean;
    snapptDocumentsVerified: boolean;
    snapptDocumentsVerifiedBy: number;
}

export enum AdditionalIncomeSourceType { 
    taxDocuments = 0,
    va_tanf_ssi = 1,
    bankStatements = 2,
    employmentOffer = 3,
    currentEmployment = 4,
    other = 5,
    PayStubs = 6,
    W2 = 7
}


export interface IdentityViewBag {
    id: string;
    account: string;
    address: Address;
    firstName: string;
    lastName: string;
    fullName: string;
    birthDate: string;
    email: string;
    phoneNumber: string;
    pictureUrl: string;
    employment_status: string;
    employmentType: string;
    jobTitle: string;
    ssn: string;
    maritalStatus: string;
    gender: string;
    hireDate: string;
    originalHireDate: string;
    terminationDate: any;
    terminationReason: any;
    employer: string;
    basePay: BasePay;
    payCycle: string;
    createdAt: Date | string;
    updatedAt: Date | string;
}

export class InviteCreateRequest {
    firstName : string;
    lastName : string;
    email: string;
    postback_url : string;
    locationId : number;
    screeningId : number;
    locationName : string;
    docsOnly: boolean;
}

export enum ArgyleUserFormType { 
    Paystub = 0,
    W2 = 1,
    BankStatement = 3
}
export class Address {
    city: string;
    line1: string;
    line2: any;
    state: string;
    country: string;
    postalCode: string;
}

export class BasePay {
    Amount: string;
    Period: string;
    Currency: string;
}

export interface IncomeStreamExclusionReason {
    id: number;
    reasonText: string;
}

export interface SopCheckResult {
    checkName: string;
    passed: boolean;
    reason: string;
}

export class ArgyleIncomeStream{
    status :string;
    firstName: string;
    lastName :string;
    streamType: IncomeStreamType;
    subType: IncomeSourceSubType;
    historicalData: any[];
    summary: RIQIncomeSummary ;
    // screening : ScreeningLight;
    screeningId : number;
    recordId : number;
    inviteId : number;
    streamIdentifier: string;
    /** Equifax TWN master transaction reference (MASTERSRVRTID). Work Number streams only. */
    masterReferenceNumber: string | null;
    exclusionReasonId: number | null;
    exclusionReasonText: string | null;
    hasDataErrors: boolean;
    errorDocCount: number;
    totalDocCount: number;
    sopChecks: SopCheckResult[] = [];

    /** Deterministic composite key for matching streams to drafts/exclusions. */
    get compositeKey(): string {
        // return `${this.streamType}|${this.subType}|${this.streamIdentifier}`;
        return `${this.streamIdentifier}`;
    }

    /** Human-readable label for this stream (employer name, applicant name, or sub-type). */
    get displayLabel(): string {
        const hist = this.historicalData?.[0] as Record<string, unknown> | undefined;
        if (hist) {
            for (const key of ['employer', 'employerName', 'employer_name', 'EmployerName']) {
                const val = hist[key];
                if (typeof val === 'string' && val.trim()) {
                    return val.trim();
                }
            }
        }
        const name = [this.firstName, this.lastName].filter(Boolean).join(' ').trim();
        return name || this.streamSubTypeString || 'Income stream';
    }

    /** Normalize field casing from API response. */
    static normalize(raw: ArgyleIncomeStream): ArgyleIncomeStream {
        const r = raw as ArgyleIncomeStream & Record<string, unknown>;
        const stream = Object.assign(new ArgyleIncomeStream(), raw);
        const streamType = r.streamType ?? r['StreamType'];
        const subType = r.subType ?? r['SubType'];
        if (streamType != null && streamType !== '') {
            stream.streamType = Number(streamType);
        }
        if (subType != null && subType !== '') {
            stream.subType = Number(subType);
        }
        if (!stream.streamIdentifier && r['StreamIdentifier']) {
            stream.streamIdentifier = String(r['StreamIdentifier']);
        }
        if (!stream.masterReferenceNumber && r['MasterReferenceNumber']) {
            stream.masterReferenceNumber = String(r['MasterReferenceNumber']);
        }
        if (!stream.inviteId && r['InviteId'] != null) {
            stream.inviteId = Number(r['InviteId']);
        }
        return stream;
    }
    
    public get streamTypeString() : string {
        if(this.streamType == 0 ) return 'Payroll Sync';
        else if(this.streamType == 1) return "Bank Account Sync";
        else if(this.streamType == 2) return "DocUpload";
        else if(this.streamType == 3) return "EFX Work Number";
        else return "Unknown";
    }
    
    
    public get streamSubTypeString() : string {
        if(this.subType == 0) return "Employment Sync";
        else if(this.subType == 1) return "Checking Sync";
        else if(this.subType == 2) return "Savings Sync";
        else if(this.subType == 3) return "Paystub Upload";
        else if(this.subType == 4) return "W2 Upload";
        else if(this.subType == 5) return "Bank Statement Upload";
        else if(this.subType == 6) return "1099 Upload";
        else return "NA";
    }
    
}

export enum IncomeStreamType
{
    PayrollSync = 0,
    BankSync = 1,
    DocUpload = 2,
    WorkNumber = 3
}

export enum IncomeSourceSubType
{
    Employment = 0,
    Checkings = 1,
    Savings = 2,
    Paystubs = 3,
    W2 = 4,
    BankStatements = 5,
    Form1099 = 6
}